import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { 
  AlertTriangle, 
  CheckCircle2, 
  Loader2, 
  Save,
  Plus,
  ChevronDown,
  ChevronUp,
  FileText,
  Check,
  ChevronsUpDown
} from "lucide-react";
import { cn } from "@/lib/utils";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format date to Italian format: dd.mm.yy
const formatDateIT = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${day}.${month}.${year.slice(2)}`;
};

// Categorie fornitori
const CATEGORIE_FORNITORI = {
  "food": "🍽️ Food & Beverage",
  "packaging": "📦 Attrezzature & Packaging",
  "servizi": "🔧 Servizi & Utenze"
};

// Numero massimo di righe visibili senza paginazione
const ITEMS_PER_PAGE = 20;

/**
 * Dialog per la revisione delle uscite estratte dall'estratto conto.
 * Permette all'utente di correggere i match automatici e confermare l'importazione.
 */
export const ReviewTransactionsDialog = ({
  open,
  onOpenChange,
  data,
  fornitori: fornitoriProp,
  tipologie,
  getAuthHeaders,
  localeId,
  onImportComplete,
  onFornitoreCreated
}) => {
  const [uscite, setUscite] = useState([]);
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);
  const [showGiaPresenti, setShowGiaPresenti] = useState(false);
  
  // Nuovo fornitore dialog
  const [newFornitoreDialog, setNewFornitoreDialog] = useState(false);
  const [newFornitoreForm, setNewFornitoreForm] = useState({
    ragione_sociale: "",
    categoria: "",
    tipologia_id: ""
  });
  const [creatingFornitore, setCreatingFornitore] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  // Paginazione
  const [currentPage, setCurrentPage] = useState(1);
  const listRef = useRef(null);

  // Lista fornitori locale (per aggiornamenti al volo)
  const [fornitori, setFornitori] = useState([]);

  // Combobox aperti per ogni riga (per gestire apertura/chiusura)
  const [openFornitoreCombobox, setOpenFornitoreCombobox] = useState({});

  // Helper: pattern per identificare una commissione bancaria
  const isCommissione = (u) =>
    !u.gia_presente &&
    typeof u.descrizione === "string" &&
    /commiss/i.test(u.descrizione);

  // Helper: uscita "certa al 100%" -> match fornitore esatto + tipologia
  // affidabile (>=95, ovvero via anagrafica fornitore o keyword esatta).
  // Le righe aggregate (commissioni) sono certe per definizione.
  const isCerta = (u) => {
    if (u.gia_presente) return false;
    if (u._aggregated) return true;
    return (u.fornitore_match_score === 100) && ((u.tipologia_match_score || 0) >= 95);
  };

  // Toggle "Mostra anche le voci certe": di default le voci certe sono nascoste
  // dalla lista di revisione per velocizzare l'import (verranno comunque
  // confermate automaticamente).
  const [mostraCerte, setMostraCerte] = useState(false);

  // Toggle "Aggrega commissioni bancarie": raggruppa per (fornitore, mese)
  // le micro-uscite con descrizione che contiene "commiss" in una singola riga.
  // Default: attivo se ci sono >= 2 commissioni nei dati originali.
  const [aggregaCommissioni, setAggregaCommissioni] = useState(false);

  // Helper: dato un array di uscite, ritorna la versione "aggregata" delle
  // commissioni bancarie (raggruppate per fornitore_id + anno/mese).
  const aggregateCommissioni = (lista) => {
    const NOMI_MESI = [
      "gennaio","febbraio","marzo","aprile","maggio","giugno",
      "luglio","agosto","settembre","ottobre","novembre","dicembre",
    ];

    const gruppi = new Map();
    const altre = [];

    lista.forEach((u) => {
      if (!isCommissione(u)) { altre.push(u); return; }
      const [y, m] = (u.data || "").split("-");
      if (!y || !m) { altre.push(u); return; }
      const key = `${u.fornitore_id || "nofor"}|${y}-${m}`;
      const cur = gruppi.get(key);
      if (cur) {
        cur.total += Number(u.importo) || 0;
        cur.count += 1;
      } else {
        gruppi.set(key, { sample: u, total: Number(u.importo) || 0, count: 1, year: y, month: m });
      }
    });

    const aggregateRows = [];
    gruppi.forEach((g) => {
      // Se solo 1 movimento nel gruppo, non c'e' bisogno di aggregare
      if (g.count <= 1) { aggregateRows.push(g.sample); return; }
      const monthIdx = parseInt(g.month, 10) - 1;
      const meseLabel = NOMI_MESI[monthIdx] || g.month;
      const lastDay = new Date(parseInt(g.year, 10), monthIdx + 1, 0)
        .toISOString().slice(0, 10);
      const forn = g.sample.fornitore_nome || "Banca";
      aggregateRows.push({
        ...g.sample,
        data: lastDay,
        importo: Math.round(g.total * 100) / 100,
        descrizione: `Commissioni bancarie ${forn} — ${meseLabel} ${g.year} (${g.count} movimenti)`,
        _aggregated: true,
        _original_count: g.count,
      });
    });

    return [...altre, ...aggregateRows];
  };

  // Conteggio commissioni nei dati originali (per la label del toggle)
  const commissioniCount = useMemo(() => {
    if (!data?.uscite) return 0;
    return data.uscite.filter(isCommissione).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Applica/rimuovi aggregazione al cambio del toggle
  useEffect(() => {
    if (!data?.uscite) return;
    const source = data.uscite.map((u) => ({ ...u }));
    const next = aggregaCommissioni ? aggregateCommissioni(source) : source;
    setUscite(next);
    const sel = {};
    next.forEach((u, i) => { if (!u.gia_presente) sel[i] = true; });
    setSelected(sel);
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregaCommissioni]);

  // Inizializza le uscite quando cambia data (solo al primo caricamento o nuovo parse)
  useEffect(() => {
    if (data?.uscite) {
      setUscite(data.uscite.map(u => ({ ...u })));
      const initialSelected = {};
      data.uscite.forEach((u, i) => {
        if (!u.gia_presente) {
          initialSelected[i] = true;
        }
      });
      setSelected(initialSelected);
      setCurrentPage(1);
      // Attiva auto-aggregazione commissioni se ce ne sono >= 2 (velocizza review)
      const nCommiss = data.uscite.filter(isCommissione).length;
      setAggregaCommissioni(nCommiss >= 2);
      // Reset toggle "Mostra anche le voci certe" su nuovo dataset
      setMostraCerte(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Aggiorna fornitori quando il parent li aggiorna (senza resettare uscite/pagina)
  useEffect(() => {
    if (fornitoriProp) {
      setFornitori([...fornitoriProp]);
    }
  }, [fornitoriProp]);

  // Fornitori ordinati alfabeticamente (lista piatta come in Prima Nota)
  const fornitoriOrdinati = useMemo(() => {
    return [...fornitori].sort((a, b) => 
      (a.ragione_sociale || a.nome || "").localeCompare(b.ragione_sociale || b.nome || "")
    );
  }, [fornitori]);

  // Tipologie ordinate
  const tipologieOrdered = useMemo(() => {
    return [...tipologie].sort((a, b) => a.nome.localeCompare(b.nome));
  }, [tipologie]);

  // Uscite filtrate per visualizzazione
  // Le voci "certe" (fornitore 100 + tipologia >= 95, e gli aggregati commissioni)
  // sono nascoste di default per velocizzare la review. Restano selezionate
  // e verranno comunque importate quando si conferma.
  const usciteDaInserire = useMemo(() => {
    const base = uscite.filter(u => !u.gia_presente);
    if (mostraCerte) return base;
    return base.filter(u => !isCerta(u));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uscite, mostraCerte]);

  // Conteggio voci certe nascoste (per il banner)
  const certeNascosteCount = useMemo(() => {
    if (mostraCerte) return 0;
    return uscite.filter(u => !u.gia_presente && isCerta(u) && !u._aggregated).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uscite, mostraCerte]);

  const aggregateNascosteCount = useMemo(() => {
    if (mostraCerte) return 0;
    return uscite.filter(u => !u.gia_presente && u._aggregated).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uscite, mostraCerte]);

  const usciteGiaPresenti = useMemo(() => 
    uscite.filter(u => u.gia_presente), [uscite]);

  // Paginazione delle uscite da inserire
  const totalPages = Math.ceil(usciteDaInserire.length / ITEMS_PER_PAGE);
  const paginatedWithOriginalIndex = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return usciteDaInserire.slice(start, start + ITEMS_PER_PAGE).map(uscita => {
      const originalIndex = uscite.indexOf(uscita);
      return { uscita, originalIndex };
    });
  }, [usciteDaInserire, uscite, currentPage]);

  // Statistiche
  const numSelezionate = Object.values(selected).filter(Boolean).length;
  const totaleSelezionato = useMemo(() => {
    let total = 0;
    uscite.forEach((u, i) => {
      if (selected[i] && !u.gia_presente) {
        total += u.importo;
      }
    });
    return total;
  }, [uscite, selected]);

  // Handlers
  const handleToggleSelect = (index) => {
    setSelected(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleSelectAll = () => {
    const newSelected = {};
    uscite.forEach((u, i) => {
      if (!u.gia_presente) {
        newSelected[i] = true;
      }
    });
    setSelected(newSelected);
  };

  const handleDeselectAll = () => {
    setSelected({});
  };

  const handleUpdateUscita = (index, field, value) => {
    setUscite(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      if (field === 'fornitore_id') {
        const forn = fornitori.find(f => f.id === value);
        updated[index].fornitore_nome = forn?.ragione_sociale || forn?.nome || null;
      }
      if (field === 'tipologia_id') {
        const tip = tipologie.find(t => t.id === value);
        updated[index].tipologia_nome = tip?.nome || null;
        
        // Salva l'associazione fornitore-tipologia per i prossimi import
        const fornitoreId = updated[index].fornitore_id;
        if (fornitoreId && value) {
          saveFornitoreTypologiaAssociation(fornitoreId, value);
        }
      }
      
      return updated;
    });
  };

  // Salva l'associazione fornitore-tipologia nel database
  const saveFornitoreTypologiaAssociation = async (fornitoreId, tipologiaId) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/fornitori/${fornitoreId}`, {
        tipologia_id: tipologiaId
      }, { headers });
      
      // Aggiorna anche la lista locale dei fornitori
      setFornitori(prev => prev.map(f => 
        f.id === fornitoreId ? { ...f, tipologia_id: tipologiaId } : f
      ));
    } catch (error) {
      console.error("Errore nel salvare l'associazione:", error);
      // Non mostriamo errore all'utente per non interrompere il flusso
    }
  };

  // Scroll to top al cambio pagina
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll alla lista
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const openNewFornitoreDialog = (index) => {
    setEditingIndex(index);
    setNewFornitoreForm({
      ragione_sociale: "",
      categoria: "",
      tipologia_id: ""
    });
    setNewFornitoreDialog(true);
  };

  const handleCreateFornitore = async (e) => {
    e.preventDefault();
    
    if (!newFornitoreForm.ragione_sociale.trim()) {
      toast.error("Inserisci la ragione sociale");
      return;
    }
    if (!newFornitoreForm.categoria) {
      toast.error("Seleziona una categoria");
      return;
    }
    
    setCreatingFornitore(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/fornitori`, {
        ragione_sociale: newFornitoreForm.ragione_sociale.trim(),
        categoria: newFornitoreForm.categoria,
        tipologia_id: newFornitoreForm.tipologia_id || null,
        locali_ids: localeId ? [localeId] : []
      }, { headers });
      
      const newFornitore = res.data;
      toast.success("Fornitore creato e selezionato");
      
      // Aggiorna lista fornitori locale
      setFornitori(prev => [...prev, newFornitore]);
      
      // Seleziona il nuovo fornitore nella riga in editing
      if (editingIndex !== null) {
        setUscite(prev => {
          const updated = [...prev];
          updated[editingIndex] = { 
            ...updated[editingIndex], 
            fornitore_id: newFornitore.id,
            fornitore_nome: newFornitore.ragione_sociale || newFornitore.nome
          };
          return updated;
        });
      }
      
      // Callback per aggiornare il parent
      onFornitoreCreated?.(newFornitore);
      
      setNewFornitoreDialog(false);
      setNewFornitoreForm({ ragione_sociale: "", categoria: "", tipologia_id: "" });
      setEditingIndex(null);
    } catch (error) {
      console.error("Errore creazione fornitore:", error);
      toast.error("Errore nella creazione del fornitore");
    } finally {
      setCreatingFornitore(false);
    }
  };

  const handleImport = async () => {
    const usciteDaImportare = [];
    
    uscite.forEach((u, i) => {
      if (selected[i] && !u.gia_presente) {
        if (!u.tipologia_id) {
          toast.error(`Seleziona una tipologia per: ${formatDateIT(u.data)} - € ${u.importo.toFixed(2)}`);
          return;
        }
        
        usciteDaImportare.push({
          data: u.data,
          importo: u.importo,
          metodo: u.metodo,
          tipologia_id: u.tipologia_id,
          fornitore_id: u.fornitore_id || null,
          descrizione: u.descrizione || ""
        });
      }
    });
    
    if (usciteDaImportare.length === 0) {
      toast.error("Nessuna uscita selezionata per l'importazione");
      return;
    }
    
    setImporting(true);
    
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/estratto-conto/import`, {
        uscite: usciteDaImportare,
        locale_id: localeId,
        banca_id: data?.banca_id || null,
        banca_nome: data?.banca_nome || "Generico"
      }, { headers });
      
      if (response.data.success) {
        toast.success(`${response.data.inserted} uscite importate con successo`);
        onImportComplete?.();
        onOpenChange(false);
      } else {
        toast.error("Errore nell'importazione");
      }
    } catch (error) {
      console.error("Errore import:", error);
      toast.error(error.response?.data?.detail || "Errore nell'importazione");
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setUscite([]);
    setSelected({});
    setShowGiaPresenti(false);
    setOpenFornitoreCombobox({});
    setCurrentPage(1);
    onOpenChange(false);
  };

  // Render match score badge
  const renderMatchBadge = (score) => {
    if (score >= 90) {
      return <Badge variant="outline" className="bg-lime-500/10 text-lime-500 border-lime-500/30 text-xs">Ottimo</Badge>;
    } else if (score >= 70) {
      return <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30 text-xs">Buono</Badge>;
    } else if (score >= 50) {
      return <Badge variant="outline" className="bg-orange-500/10 text-orange-500 border-orange-500/30 text-xs">Incerto</Badge>;
    } else {
      return <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30 text-xs">Mancante</Badge>;
    }
  };

  if (!data) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] bg-card border-border overflow-hidden flex flex-col" data-testid="review-transactions-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-foreground flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Revisione Estratto Conto
            </DialogTitle>
            <p className="text-xs text-muted-foreground" data-testid="review-statement-bank">
              Banca: <strong>{data?.banca_nome || "Generico"}</strong>
            </p>
          </DialogHeader>

          {/* Verifica Totali */}
          {data.totale_uscite_pdf && (
            <div className={`p-3 rounded-xl border shrink-0 ${
              Math.abs(data.totale_uscite - data.totale_uscite_pdf) < 1 
                ? 'bg-lime-500/10 border-lime-500/30' 
                : 'bg-red-500/10 border-red-500/30'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Verifica Totale</p>
                  <p className="text-sm">
                    <span className="font-medium">Calcolato:</span> € {data.totale_uscite?.toFixed(2)} | 
                    <span className="font-medium ml-2">PDF:</span> € {data.totale_uscite_pdf?.toFixed(2)}
                  </p>
                </div>
                {Math.abs(data.totale_uscite - data.totale_uscite_pdf) < 1 ? (
                  <CheckCircle2 className="h-5 w-5 text-lime-500" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                )}
              </div>
            </div>
          )}

          {/* Statistiche */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 py-3 shrink-0">
            <div className="bg-card border border-border rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-foreground">{data.num_uscite}</p>
              <p className="text-xs text-muted-foreground">Uscite totali</p>
            </div>
            <div className="bg-lime-500/10 border border-lime-500/30 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-lime-500">{data.num_da_inserire}</p>
              <p className="text-xs text-lime-500/70">Da inserire</p>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-amber-500">{data.num_gia_presenti}</p>
              <p className="text-xs text-amber-500/70">Già presenti</p>
            </div>
            <div className="bg-primary/10 border border-primary/30 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-primary">€ {totaleSelezionato.toFixed(2)}</p>
              <p className="text-xs text-primary/70">Selezionato</p>
            </div>
          </div>

          {/* Nuovi fornitori suggeriti */}
          {data.nuovi_fornitori_suggeriti?.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 shrink-0">
              <p className="text-sm text-amber-500 font-medium mb-2">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                Nuovi fornitori suggeriti:
              </p>
              <div className="flex flex-wrap gap-2">
                {data.nuovi_fornitori_suggeriti.map((nome, i) => (
                  <Badge key={i} variant="outline" className="bg-card text-foreground">
                    {nome}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Banner voci certe auto-importate (nascoste dalla review) */}
          {(certeNascosteCount > 0 || aggregateNascosteCount > 0) && (
            <div
              className="flex items-start gap-3 py-3 px-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl shrink-0"
              data-testid="voci-certe-banner"
            >
              <div className="text-2xl shrink-0">✨</div>
              <div className="flex-1 text-sm">
                <div className="font-medium text-foreground">
                  Import veloce attivo
                </div>
                <div className="text-muted-foreground mt-0.5">
                  {certeNascosteCount > 0 && (
                    <span data-testid="certe-count">
                      <b className="text-emerald-500">{certeNascosteCount}</b>{" "}
                      {certeNascosteCount === 1 ? "voce certa" : "voci certe"}
                    </span>
                  )}
                  {certeNascosteCount > 0 && aggregateNascosteCount > 0 && " + "}
                  {aggregateNascosteCount > 0 && (
                    <span data-testid="aggregate-count">
                      <b className="text-violet-500">{aggregateNascosteCount}</b>{" "}
                      {aggregateNascosteCount === 1 ? "commissione aggregata" : "commissioni aggregate"}
                    </span>
                  )}{" "}
                  verranno importate automaticamente. Sotto vedi solo le righe da
                  verificare.
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMostraCerte(true)}
                className="h-8 text-xs shrink-0 text-emerald-500 hover:text-emerald-600 hover:bg-emerald-500/10"
                data-testid="mostra-certe-btn"
              >
                Mostra anche queste
              </Button>
            </div>
          )}
          {mostraCerte && (certeNascosteCount === 0 && aggregateNascosteCount === 0) === false && (
            <div className="flex items-center justify-between py-2 px-3 bg-muted/40 rounded-lg shrink-0" data-testid="mostra-tutte-row">
              <span className="text-xs text-muted-foreground">
                Stai vedendo tutte le righe (anche quelle certe).
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMostraCerte(false)}
                className="h-7 text-xs"
                data-testid="nascondi-certe-btn"
              >
                Nascondi le certe
              </Button>
            </div>
          )}

          {/* Toggle aggregazione commissioni bancarie */}
          {commissioniCount >= 2 && (
            <div
              className="flex items-center gap-3 py-2 px-3 bg-violet-500/10 border border-violet-500/30 rounded-xl shrink-0"
              data-testid="aggrega-commissioni-toggle-row"
            >
              <Checkbox
                id="aggrega-commissioni"
                checked={aggregaCommissioni}
                onCheckedChange={(v) => setAggregaCommissioni(Boolean(v))}
                data-testid="aggrega-commissioni-checkbox"
              />
              <Label
                htmlFor="aggrega-commissioni"
                className="text-sm text-foreground cursor-pointer flex-1"
              >
                Aggrega commissioni bancarie
                <span className="text-muted-foreground ml-1">
                  ({commissioniCount} movimenti -&gt; 1 riga per fornitore/mese)
                </span>
              </Label>
            </div>
          )}

          {/* Actions bar */}
          <div className="flex items-center justify-between py-2 shrink-0 border-b border-border">
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSelectAll}
                className="text-xs border-border text-foreground hover:bg-accent"
              >
                Seleziona tutte
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleDeselectAll}
                className="text-xs text-muted-foreground"
              >
                Deseleziona
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              {numSelezionate} selezionate
              {(certeNascosteCount > 0 || aggregateNascosteCount > 0) ? (
                <span className="ml-1">
                  ({usciteDaInserire.length} in lista
                  {certeNascosteCount > 0 && ` + ${certeNascosteCount} certe`}
                  {aggregateNascosteCount > 0 && ` + ${aggregateNascosteCount} aggregata${aggregateNascosteCount === 1 ? '' : 'e'}`}
                  )
                </span>
              ) : (
                <span> su {usciteDaInserire.length}</span>
              )}
            </p>
          </div>

          {/* Paginazione top */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 py-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="text-xs border-border text-foreground"
              >
                Precedente
              </Button>
              <span className="text-sm text-muted-foreground">
                Pagina {currentPage} di {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="text-xs border-border text-foreground"
              >
                Successiva
              </Button>
            </div>
          )}

          {/* Lista uscite da inserire (paginate) */}
          <div ref={listRef} className="flex-1 overflow-y-auto space-y-2 py-2 min-h-0">
            {usciteDaInserire.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-lime-500" />
                <p>Tutte le uscite sono già presenti nel database!</p>
              </div>
            ) : (
              paginatedWithOriginalIndex.map(({ uscita, originalIndex }) => {
                const isSelected = selected[originalIndex];
                
                return (
                  <div 
                    key={originalIndex}
                    className={`
                      border rounded-xl p-3 transition-all
                      ${isSelected 
                        ? 'border-primary/50 bg-primary/5' 
                        : 'border-border bg-card hover:border-border/80'
                      }
                    `}
                    data-testid={`review-transaction-${originalIndex}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleToggleSelect(originalIndex)}
                        className="mt-1"
                        data-testid={`review-transaction-checkbox-${originalIndex}`}
                      />
                      
                      {/* Info principali */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-foreground">
                              {formatDateIT(uscita.data)}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {uscita.metodo}
                            </Badge>
                          </div>
                          <span className="font-bold text-red-400 text-lg">
                            € {uscita.importo.toFixed(2)}
                          </span>
                        </div>
                        
                        <p className="text-sm text-muted-foreground truncate mb-3">
                          {uscita.descrizione}
                        </p>
                        
                        {/* Selettori */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {/* Fornitore - Combobox con ricerca */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs text-muted-foreground">Fornitore</Label>
                              <div className="flex items-center gap-2">
                                {renderMatchBadge(uscita.fornitore_match_score)}
                                <button
                                  type="button"
                                  onClick={() => openNewFornitoreDialog(originalIndex)}
                                  className="text-[10px] text-primary hover:text-primary/80 hover:underline flex items-center gap-0.5"
                                  data-testid={`add-fornitore-btn-${originalIndex}`}
                                >
                                  <Plus className="h-3 w-3" />
                                  Nuovo
                                </button>
                              </div>
                            </div>
                            <Popover 
                              open={openFornitoreCombobox[originalIndex] ?? false} 
                              onOpenChange={(isOpen) => setOpenFornitoreCombobox(prev => ({ ...prev, [originalIndex]: isOpen }))}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  aria-expanded={openFornitoreCombobox[originalIndex] ?? false}
                                  className="w-full h-9 justify-between bg-card border-border text-foreground hover:bg-accent hover:text-accent-foreground"
                                  data-testid={`fornitore-combobox-${originalIndex}`}
                                >
                                  <span className="truncate">
                                    {uscita.fornitore_id
                                      ? (fornitori.find(f => f.id === uscita.fornitore_id)?.ragione_sociale || 
                                         fornitori.find(f => f.id === uscita.fornitore_id)?.nome || 
                                         "Seleziona...")
                                      : "Seleziona fornitore..."}
                                  </span>
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[280px] p-0" align="start">
                                <Command>
                                  <CommandInput placeholder="Cerca fornitore..." className="h-9" />
                                  <CommandList>
                                    <CommandEmpty>Nessun fornitore trovato</CommandEmpty>
                                    {/* Opzione Nessuno */}
                                    <CommandGroup>
                                      <CommandItem
                                        value="__nessuno__"
                                        onSelect={() => {
                                          handleUpdateUscita(originalIndex, 'fornitore_id', null);
                                          setOpenFornitoreCombobox(prev => ({ ...prev, [originalIndex]: false }));
                                        }}
                                        className="cursor-pointer"
                                      >
                                        <Check className={cn("mr-2 h-4 w-4", !uscita.fornitore_id ? "opacity-100" : "opacity-0")} />
                                        <span className="text-muted-foreground">- Nessuno -</span>
                                      </CommandItem>
                                    </CommandGroup>
                                    {/* Fornitori - lista piatta alfabetica */}
                                    <CommandGroup heading="Fornitori">
                                      {fornitoriOrdinati.map((f) => (
                                        <CommandItem
                                          key={f.id}
                                          value={f.ragione_sociale || f.nome}
                                          onSelect={() => {
                                            handleUpdateUscita(originalIndex, 'fornitore_id', f.id);
                                            setOpenFornitoreCombobox(prev => ({ ...prev, [originalIndex]: false }));
                                          }}
                                          className="cursor-pointer"
                                        >
                                          <Check className={cn("mr-2 h-4 w-4", uscita.fornitore_id === f.id ? "opacity-100" : "opacity-0")} />
                                          {f.ragione_sociale || f.nome}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>
                          
                          {/* Tipologia */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs text-muted-foreground">Tipologia *</Label>
                              {renderMatchBadge(uscita.tipologia_match_score)}
                            </div>
                            <Select 
                              value={uscita.tipologia_id || ""} 
                              onValueChange={(v) => handleUpdateUscita(originalIndex, 'tipologia_id', v)}
                            >
                              <SelectTrigger className={`h-9 bg-card border-border text-foreground ${!uscita.tipologia_id ? 'border-red-500/50' : ''}`}>
                                <SelectValue placeholder="Seleziona tipologia" />
                              </SelectTrigger>
                              <SelectContent className="max-h-[300px]">
                                {tipologieOrdered.map((t) => (
                                  <SelectItem key={t.id} value={t.id}>
                                    {t.nome}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Già presenti (collapsible) */}
          {usciteGiaPresenti.length > 0 && (
            <div className="shrink-0 border-t border-border pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowGiaPresenti(!showGiaPresenti)}
                className="w-full justify-between text-amber-500"
              >
                <span>
                  <AlertTriangle className="h-4 w-4 inline mr-2" />
                  {usciteGiaPresenti.length} uscite già presenti (ignorate)
                </span>
                {showGiaPresenti ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
              
              {showGiaPresenti && (
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {usciteGiaPresenti.map((u, i) => (
                    <div key={i} className="flex items-center justify-between py-1 px-2 bg-amber-500/5 rounded text-sm">
                      <span className="text-muted-foreground">
                        {formatDateIT(u.data)} - {u.descrizione?.slice(0, 40)}...
                      </span>
                      <span className="text-amber-500 font-medium">€ {u.importo.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="shrink-0 pt-4 border-t border-border">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={importing}
              className="border-border text-foreground hover:bg-accent hover:text-accent-foreground"
            >
              Annulla
            </Button>
            <Button 
              onClick={handleImport}
              disabled={numSelezionate === 0 || importing}
              className="bg-lime-500 hover:bg-lime-400 text-black"
              data-testid="review-transactions-import-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importazione...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Importa {numSelezionate} uscite
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Nuovo Fornitore */}
      <Dialog open={newFornitoreDialog} onOpenChange={setNewFornitoreDialog}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              Nuovo Fornitore
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateFornitore} className="space-y-4">
            {/* Ragione Sociale */}
            <div className="space-y-2">
              <Label className="text-foreground/70">Ragione Sociale *</Label>
              <Input 
                value={newFornitoreForm.ragione_sociale} 
                onChange={(e) => setNewFornitoreForm(prev => ({ ...prev, ragione_sociale: e.target.value }))} 
                placeholder="Nome del fornitore" 
                autoFocus 
                className="bg-card border-border text-foreground" 
              />
            </div>
            
            {/* Categoria */}
            <div className="space-y-2">
              <Label className="text-foreground/70">Categoria *</Label>
              <Select 
                value={newFornitoreForm.categoria} 
                onValueChange={(v) => setNewFornitoreForm(prev => ({ ...prev, categoria: v }))}
              >
                <SelectTrigger className="bg-card border-border text-foreground">
                  <SelectValue placeholder="Seleziona categoria" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORIE_FORNITORI).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Tipologia */}
            <div className="space-y-2">
              <Label className="text-foreground/70">Tipologia Uscita</Label>
              <Select 
                value={newFornitoreForm.tipologia_id} 
                onValueChange={(v) => setNewFornitoreForm(prev => ({ ...prev, tipologia_id: v }))}
              >
                <SelectTrigger className="bg-card border-border text-foreground">
                  <SelectValue placeholder="Seleziona tipologia (opzionale)" />
                </SelectTrigger>
                <SelectContent>
                  {[...tipologie].sort((a, b) => a.nome.localeCompare(b.nome)).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setNewFornitoreDialog(false)} 
                disabled={creatingFornitore}
                className="border-border text-foreground hover:bg-accent hover:text-accent-foreground"
              >
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={creatingFornitore}
                className="bg-lime-500 hover:bg-lime-400 text-black"
              >
                {creatingFornitore ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creazione...
                  </>
                ) : (
                  "Crea Fornitore"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
